/**
 * AQI scoring panel — the Content Engine's per-draft quality actions.
 *
 * Two pieces:
 *   - AqiBreakdownCard: a glass breakdown of one AQI read — a total dial
 *     (0–100) plus the six dimension bars — with the standard 'Simulated'
 *     pill when the read came from the local heuristic instead of the
 *     deployed argoiq-engine.
 *   - AqiScorePanel: the stateful actions — 'Score (AQI)' and
 *     'Fix & re-score' (before/after receipt with the lift highlighted,
 *     plus an operator-clicked "Use fixed draft" apply), and — when the
 *     host view supplies `ensurePublishDraft` on a publishable channel —
 *     the born-scored Publish gate: disabled until scored, an explicit
 *     'Publish anyway (below bar)' extra confirm below the AQI bar, and
 *     either path only QUEUES a publish_content proposal. The approval
 *     inbox is the final gate; nothing posts without a human approving.
 *
 * All engine access goes through the src/lib/agent/contentEngine.ts seam,
 * so configured vs simulated is decided in exactly one place. Publishing is
 * NEVER simulated — without the engine connection the gate says so honestly.
 */

import { useState } from 'react';
import {
  AlertTriangle,
  Gauge,
  Loader2,
  Send,
  TrendingDown,
  TrendingUp,
  Wand2,
  Minus,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import {
  describePublishOutcome,
  fixContent,
  fixReceipt,
  isConfigured,
  isPublishChannel,
  scoreContent,
  type AqiFixResult,
  type AqiScoreResult,
  type ContentChannel,
  type PublishStatus,
} from '../lib/agent/contentEngine';
import {
  evaluatePublishGate,
  publishButtonLabel,
} from '../lib/content/publishGate';
import { queueApproval } from '../lib/agent/approvals';

/** Total → accent color band (mirrors the view's grade coloring). */
function totalColor(total: number): string {
  if (total >= 80) return C.green;
  if (total >= 60) return C.blueElectric;
  if (total >= 40) return C.orange;
  return C.orangeWarm;
}

/** The standard honesty pill for simulated reads. */
function SimulatedPill() {
  return (
    <span
      className="glass-pill"
      title="No engine configured — deterministic local heuristic, not a real AQI read."
      style={{
        fontSize: 11,
        padding: '3px 10px',
        background: 'rgba(180,188,210,0.15)',
        color: C.textMuted,
        flexShrink: 0,
      }}
    >
      Simulated
    </span>
  );
}

/** SVG total dial — a 0–100 arc with the number in the middle. */
function TotalDial({ total }: { total: number }) {
  const r = 26;
  const circumference = 2 * Math.PI * r;
  const color = totalColor(total);
  return (
    <div
      role="meter"
      aria-valuenow={total}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="AQI total"
      style={{ position: 'relative', width: 68, height: 68, flexShrink: 0 }}
    >
      <svg width={68} height={68} viewBox="0 0 68 68" aria-hidden="true">
        <circle cx={34} cy={34} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={5} />
        <circle
          cx={34}
          cy={34}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={5}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - total / 100)}
          transform="rotate(-90 34 34)"
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          color,
          fontWeight: 700,
          fontSize: 16,
        }}
      >
        {total}
      </div>
    </div>
  );
}

/** Glass breakdown card: total dial + six dimension bars (+ Simulated pill). */
export function AqiBreakdownCard({
  result,
  title = 'AQI score',
}: {
  result: AqiScoreResult;
  title?: string;
}) {
  return (
    <GlassPanel style={{ padding: 14, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Gauge size={16} style={{ color: totalColor(result.total), flexShrink: 0 }} />
        <span className="t-card-title" style={{ color: C.text, flex: 1 }}>
          {title}
        </span>
        {result.simulated && <SimulatedPill />}
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <TotalDial total={result.total} />
        <div style={{ display: 'grid', gap: 6, flex: 1, minWidth: 0 }}>
          {result.dimensions.map((d) => {
            const pct = d.max > 0 ? d.score / d.max : 0;
            const barColor = pct >= 0.6 ? C.green : pct >= 0.35 ? C.orange : C.orangeWarm;
            return (
              <div key={d.key} style={{ display: 'grid', gap: 2 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span className="t-meta" style={{ fontSize: 11, color: C.text }}>
                    {d.label}
                  </span>
                  <span className="t-meta" style={{ marginLeft: 'auto', color: C.textMuted }}>
                    {d.score}/{d.max}
                  </span>
                </div>
                <div
                  style={{
                    height: 4,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.08)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${Math.round(pct * 100)}%`,
                      height: '100%',
                      borderRadius: 2,
                      background: barColor,
                    }}
                  />
                </div>
              </div>
            );
          })}
          {result.dimensions.length === 0 && (
            <div className="t-meta" style={{ color: C.textDim }}>
              No per-dimension breakdown returned — total only.
            </div>
          )}
        </div>
      </div>
    </GlassPanel>
  );
}

/** Before → after receipt for a fix, lift highlighted. */
export function FixReceipt({
  before,
  after,
}: {
  before: AqiScoreResult;
  after: AqiScoreResult;
}) {
  const receipt = fixReceipt(before, after);
  const liftColor = receipt.lift > 0 ? C.green : receipt.lift < 0 ? C.orangeWarm : C.textMuted;
  const LiftIcon = receipt.lift > 0 ? TrendingUp : receipt.lift < 0 ? TrendingDown : Minus;
  return (
    <div
      className="glass-row"
      data-testid="aqi-fix-receipt"
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 2px' }}
    >
      <span className="t-meta" style={{ color: C.textMuted }}>
        {receipt.before}
      </span>
      <span className="t-meta" style={{ color: C.textDim }}>
        →
      </span>
      <span className="t-card-title" style={{ color: totalColor(receipt.after) }}>
        {receipt.after}
      </span>
      <span
        className="glass-pill"
        style={{
          marginLeft: 'auto',
          fontSize: 11,
          padding: '3px 10px',
          fontWeight: 600,
          background: `${liftColor}22`,
          color: liftColor,
          boxShadow: `inset 0 0 0 1px ${liftColor}55`,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <LiftIcon size={12} />
        {receipt.lift > 0 ? `+${receipt.lift}` : receipt.lift}
      </span>
    </div>
  );
}

/**
 * Honest render of a draft's most recent engine publish outcome — the exact
 * same wording the executor recorded ('DRY RUN (no live post)' vs
 * 'Published to LinkedIn' + url vs the error). Shared by the workspace so a
 * dry run can never be dressed up as a live post.
 */
export function PublishOutcomeNote({
  publish,
}: {
  publish: {
    status: PublishStatus;
    url: string | null;
    message: string | null;
    channel: string;
    at: string;
  };
}) {
  const channel = isPublishChannel(publish.channel) ? publish.channel : 'linkedin';
  const label = describePublishOutcome(publish, channel);
  const color =
    publish.status === 'published'
      ? C.green
      : publish.status === 'dry_run'
        ? C.blueElectric
        : C.orangeWarm;
  return (
    <div
      className="glass-row"
      data-testid="publish-outcome"
      style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
    >
      <span
        className="glass-pill"
        style={{
          fontSize: 11,
          padding: '3px 10px',
          fontWeight: 600,
          background: `${color}22`,
          color,
          boxShadow: `inset 0 0 0 1px ${color}55`,
        }}
      >
        {label}
      </span>
      {publish.status === 'published' && publish.url && (
        <a
          href={publish.url}
          target="_blank"
          rel="noreferrer"
          className="t-meta"
          style={{ color: C.blueElectric }}
        >
          View post
        </a>
      )}
      <span className="t-meta" style={{ color: C.textDim, marginLeft: 'auto' }}>
        {new Date(publish.at).toLocaleString()}
      </span>
    </div>
  );
}

/**
 * The per-draft AQI actions: Score (AQI) + Fix & re-score. `onApplyFix`
 * (operator-clicked, never automatic) replaces the visible draft with the
 * fixed version. `ensurePublishDraft` (optional) enables the born-scored
 * Publish gate: the host view resolves/persists the draft record and this
 * panel queues the governed publish_content proposal.
 */
export function AqiScorePanel({
  content,
  channel,
  customer,
  onApplyFix,
  ensurePublishDraft,
}: {
  content: string;
  channel: ContentChannel;
  customer?: string;
  onApplyFix?: (text: string) => void;
  /**
   * Resolve (creating/updating as needed) the persisted draft record the
   * publish outcome will be stamped on. Its presence — on a publishable
   * channel — is what renders the Publish gate.
   */
  ensurePublishDraft?: () => Promise<{ id: string; title: string }>;
}) {
  const [busy, setBusy] = useState<'score' | 'fix' | null>(null);
  const [score, setScore] = useState<AqiScoreResult | null>(null);
  const [fix, setFix] = useState<{ before: AqiScoreResult; result: AqiFixResult } | null>(null);
  const [confirmBelow, setConfirmBelow] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [queuedNote, setQueuedNote] = useState<string | null>(null);

  const disabled = !content.trim() || busy !== null;

  // Born-scored gate: no score → no publish. Below the bar → extra confirm.
  const gate = evaluatePublishGate(score?.total ?? null);
  const publishable = ensurePublishDraft != null && isPublishChannel(channel);
  const engineReady = isConfigured();

  const queuePublish = async () => {
    if (!ensurePublishDraft || !isPublishChannel(channel) || score == null) return;
    setQueueing(true);
    try {
      const draftRef = await ensurePublishDraft();
      // Queue only — the approval inbox is the final gate. `dryRun` is
      // deliberately OMITTED so the engine's server-side safety default rules.
      await queueApproval(
        {
          kind: 'publish_content',
          draftId: draftRef.id,
          draftTitle: draftRef.title,
          channel,
          contentSnapshot: content,
          aqiTotal: score.total,
          belowThreshold: gate.belowThreshold,
          ...(customer ? { customer } : {}),
        },
        'chat',
        'Content workspace publish request',
        {
          rationale: gate.reason,
          evidence: [
            `AQI ${score.total} vs publish bar ${gate.threshold}`,
            `Channel: ${channel}`,
            ...(score.simulated ? ['Score was SIMULATED (no engine read)'] : []),
          ],
        },
      );
      setQueuedNote(
        'Queued for approval — the approval inbox is the final gate. Nothing posts until a human approves.',
      );
      setConfirmBelow(false);
    } finally {
      setQueueing(false);
    }
  };

  const handleScore = async () => {
    setBusy('score');
    setFix(null);
    try {
      setScore(await scoreContent({ content, channel, customer }));
    } finally {
      setBusy(null);
    }
  };

  const handleFix = async () => {
    setBusy('fix');
    try {
      const before = score ?? (await scoreContent({ content, channel, customer }));
      const result = await fixContent({ content, channel, customer });
      setScore(result.score);
      setFix({ before, result });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={handleScore}
          disabled={disabled}
          className="glass-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          {busy === 'score' ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Gauge size={13} />
          )}
          Score (AQI)
        </button>
        <button
          onClick={handleFix}
          disabled={disabled}
          className="glass-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          {busy === 'fix' ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Wand2 size={13} />
          )}
          Fix &amp; re-score
        </button>
        {!isConfigured() && <SimulatedPill />}
      </div>

      {fix && (
        <GlassPanel style={{ padding: 12, display: 'grid', gap: 8 }}>
          <div className="t-eyebrow">Fix receipt</div>
          <FixReceipt before={fix.before} after={fix.result.score} />
          <pre
            style={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: 12,
              lineHeight: 1.5,
              color: C.text,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10,
              padding: 10,
              maxHeight: 220,
              overflow: 'auto',
            }}
          >
            {fix.result.content}
          </pre>
          {onApplyFix && (
            <button
              onClick={() => onApplyFix(fix.result.content)}
              className="glass-btn"
              style={{ justifySelf: 'start' }}
            >
              Use fixed draft
            </button>
          )}
        </GlassPanel>
      )}

      {score && <AqiBreakdownCard result={score} />}

      {publishable && (
        <GlassPanel style={{ padding: 12, display: 'grid', gap: 8 }}>
          <div className="t-eyebrow">Publish · born-scored gate</div>
          {!engineReady ? (
            // Honest unconfigured state — a simulated 'published' is a lie.
            <p className="t-meta" style={{ margin: 0, color: C.textMuted }}>
              Publishing requires the engine connection — set{' '}
              <code style={{ fontSize: 10, opacity: 0.7 }}>VITE_CONTENT_ENGINE_URL</code>. There
              is no simulated publish.
            </p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  className="glass-btn"
                  disabled={!gate.canPublish || queueing || !content.trim()}
                  title={gate.reason}
                  onClick={() => {
                    if (gate.requiresExtraConfirm) setConfirmBelow(true);
                    else void queuePublish();
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  {queueing ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Send size={13} />
                  )}
                  {publishButtonLabel(gate)}
                </button>
                <span className="t-meta" style={{ color: C.textMuted }}>
                  {gate.reason}
                </span>
              </div>

              {confirmBelow && gate.state === 'below_bar' && (
                <div
                  className="glass-row"
                  data-testid="publish-below-bar-confirm"
                  style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <AlertTriangle size={14} style={{ color: C.orange, flexShrink: 0 }} />
                  <span className="t-meta" style={{ color: C.text }}>
                    AQI {gate.aqiTotal} is below the {gate.threshold} bar. Queue for publish
                    anyway?
                  </span>
                  <button
                    className="glass-btn"
                    disabled={queueing}
                    onClick={() => void queuePublish()}
                    style={{ color: C.orange }}
                  >
                    Confirm — publish below bar
                  </button>
                  <button
                    className="glass-btn"
                    disabled={queueing}
                    onClick={() => setConfirmBelow(false)}
                  >
                    Cancel
                  </button>
                </div>
              )}

              {queuedNote && (
                <p className="t-meta" style={{ margin: 0, color: C.green }}>
                  {queuedNote}
                </p>
              )}
            </>
          )}
        </GlassPanel>
      )}
    </div>
  );
}
